import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Link2,
  Plus,
  XCircle,
} from 'lucide-react';
import { aiPurchasesAPI, suppliersAPI } from '../../api';
import SupplierPicker from '../SupplierPicker';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent } from '../ui/Card';
import { useToast } from '../../hooks/useToast';

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function formatStatementDate(value) {
  if (!value) return '—';
  return new Date(value).toISOString().slice(0, 10);
}

export function parseExtractedData(draft) {
  if (!draft?.extracted_data) return null;
  return typeof draft.extracted_data === 'string'
    ? JSON.parse(draft.extracted_data)
    : draft.extracted_data;
}

function buildSupplierFormFromExtracted(extracted) {
  if (!extracted?.supplier_name) return {};
  return {
    supplier_name: extracted.supplier_name || '',
    supplier_code: extracted.supplier_code || '',
    contact_name: extracted.supplier_contact_name || extracted.contact_name || '',
    email: extracted.supplier_email || extracted.email || '',
    phone: extracted.supplier_phone || extracted.phone || '',
    address_line1: extracted.supplier_address_line1 || extracted.address_line1 || '',
    address_line2: extracted.supplier_address_line2 || extracted.address_line2 || '',
    city: extracted.supplier_city || extracted.city || '',
    county: extracted.supplier_county || extracted.county || '',
    postcode: extracted.supplier_postcode || extracted.postcode || '',
    country: extracted.supplier_country || extracted.country || 'GB',
    payment_terms: extracted.payment_terms || '',
  };
}

function BankLineContextCard({ statementLine }) {
  const lineAmount = Number(statementLine?.amount ?? 0);

  return (
    <Card className="border-[var(--ds-border-subtle)]">
      <CardContent className="p-4">
        <h3 className="text-h4 font-medium text-[var(--ds-text-primary)] mb-3">
          Bank statement line
        </h3>
        <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Date
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              {formatStatementDate(statementLine?.transaction_date)}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Description
            </div>
            <div
              className="truncate text-gray-900 dark:text-gray-100"
              title={statementLine?.description || ''}
            >
              {statementLine?.description || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Reference
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              {statementLine?.reference || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Counterparty
            </div>
            <div className="truncate text-gray-900 dark:text-gray-100">
              {statementLine?.counterparty || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Bank amount
            </div>
            <div
              className={
                lineAmount >= 0
                  ? 'font-semibold text-green-700 dark:text-green-400'
                  : 'font-semibold text-red-700 dark:text-red-400'
              }
            >
              {formatMoney(lineAmount)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AmountVarianceBanner({ statementLine, extractedTotal }) {
  const lineAmount = Number(statementLine?.amount ?? 0);
  const receiptTotal = Number(extractedTotal);
  const bankAbs = Math.abs(lineAmount);
  const variance = Math.abs(receiptTotal - bankAbs);

  if (!Number.isFinite(receiptTotal) || !Number.isFinite(bankAbs) || variance <= 0.01) {
    return null;
  }

  return (
    <Card className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
      <CardContent className="flex gap-2 p-3 text-xs text-amber-800 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <div>
          <p className="font-medium">Amount variance</p>
          <p className="mt-1">
            Receipt total {formatMoney(receiptTotal)} differs from bank line{' '}
            {formatMoney(bankAbs)} by {formatMoney(variance)}. You can still approve and
            reconcile using the bank amount.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Shared AI purchase draft review UI — extracted fields, supplier match/create,
 * approve/reject, and optional bank-line context for reconciliation flows.
 */
export function AIDraftReviewPanel({
  draft,
  statementLine,
  onApprove,
  onReject,
  onDraftUpdated,
  showDownloadFile = true,
  approving = false,
  rejecting = false,
  showActions = true,
  /** Optional parent toast helpers so messages share one ToastContainer */
  toast: toastProp,
}) {
  const internalToast = useToast();
  const success = toastProp?.success ?? internalToast.success;
  const error = toastProp?.error ?? internalToast.error;
  const PanelToastContainer = toastProp ? null : internalToast.ToastContainer;
  const [showSupplierPicker, setShowSupplierPicker] = useState(false);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({});
  const [creatingSupplier, setCreatingSupplier] = useState(false);

  const extractedData = useMemo(() => parseExtractedData(draft), [draft]);

  useEffect(() => {
    setSupplierFormData(buildSupplierFormFromExtracted(extractedData));
  }, [draft?.id, extractedData]);

  const reloadDraft = async () => {
    if (!draft?.id) return null;
    const result = await aiPurchasesAPI.getDraft(draft.id);
    onDraftUpdated?.(result.draft);
    return result.draft;
  };

  const handleSupplierSelect = async (supplier) => {
    if (!draft?.id) return;

    try {
      await aiPurchasesAPI.update(draft.id, { supplier_id: supplier.id });
      success(`Supplier "${supplier.supplier_name}" linked to draft`);
      setShowSupplierPicker(false);
      await reloadDraft();
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to link supplier');
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();

    if (!supplierFormData.supplier_name?.trim()) {
      error('Supplier name is required');
      return;
    }

    setCreatingSupplier(true);
    try {
      const response = await suppliersAPI.create(supplierFormData);
      const newSupplier = response.supplier || response;

      await aiPurchasesAPI.update(draft.id, { supplier_id: newSupplier.id });

      success(`Supplier "${newSupplier.supplier_name}" created and linked to draft`);
      setShowCreateSupplier(false);
      await reloadDraft();
    } catch (err) {
      error(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          'Failed to create supplier'
      );
    } finally {
      setCreatingSupplier(false);
    }
  };

  const handleSupplierFormChange = (e) => {
    const { name, value } = e.target;
    setSupplierFormData((prev) => ({ ...prev, [name]: value }));
  };

  const canApproveOrReject =
    draft?.status === 'completed' || draft?.status === 'needs_review';

  if (!draft) return null;

  return (
    <>
      <div className="space-y-4">
        {statementLine && <BankLineContextCard statementLine={statementLine} />}

        {statementLine && extractedData?.total != null && (
          <AmountVarianceBanner
            statementLine={statementLine}
            extractedTotal={extractedData.total}
          />
        )}

        {extractedData && (
          <div>
            <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
              Extracted Data
            </h3>
            <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4 space-y-2">
              {extractedData.supplier_name && (
                <div className="space-y-2">
                  <div>
                    <span className="text-meta text-[var(--ds-text-secondary)]">
                      Extracted Supplier:{' '}
                    </span>
                    <span className="text-body text-[var(--ds-text-primary)] font-medium">
                      {extractedData.supplier_name}
                    </span>
                  </div>
                  {draft.supplier_id ? (
                    <div className="space-y-2 pt-2 border-t border-[var(--ds-border-subtle)]">
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        <span className="text-[var(--ds-text-secondary)]">
                          Matched to:{' '}
                          <strong className="text-[var(--ds-text-primary)]">
                            {draft.supplier_name || 'Supplier'}
                          </strong>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowSupplierPicker(true)}
                          className="flex-1"
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Change Match
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowCreateSupplier(true)}
                          className="flex-1"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Create New
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 pt-2 border-t border-[var(--ds-border-subtle)]">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowSupplierPicker(true)}
                        className="flex-1"
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        Match Supplier
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowCreateSupplier(true)}
                        className="flex-1"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Create Supplier
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {extractedData.invoice_number && (
                <div>
                  <span className="text-meta text-[var(--ds-text-secondary)]">Invoice #: </span>
                  <span className="text-body text-[var(--ds-text-primary)]">
                    {extractedData.invoice_number}
                  </span>
                </div>
              )}
              {extractedData.purchase_date && (
                <div>
                  <span className="text-meta text-[var(--ds-text-secondary)]">Date: </span>
                  <span className="text-body text-[var(--ds-text-primary)]">
                    {extractedData.purchase_date}
                  </span>
                </div>
              )}
              {extractedData.total != null && (
                <div>
                  <span className="text-meta text-[var(--ds-text-secondary)]">Total: </span>
                  <span className="text-body text-[var(--ds-text-primary)] font-medium">
                    {formatMoney(extractedData.total)}
                  </span>
                </div>
              )}
              {extractedData.items?.length > 0 && (
                <div className="mt-3">
                  <span className="text-meta text-[var(--ds-text-secondary)]">Items: </span>
                  <span className="text-body text-[var(--ds-text-primary)]">
                    {extractedData.items.length} line item(s)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {extractedData?.items?.length > 0 && (
          <div>
            <h4 className="text-h4 font-medium text-[var(--ds-text-primary)] mb-2">
              Line Items
            </h4>
            <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4">
              <div className="space-y-2">
                {extractedData.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="border-b border-[var(--ds-border-subtle)] pb-2 last:border-0"
                  >
                    <p className="text-body text-[var(--ds-text-primary)] font-medium">
                      {item.description}
                    </p>
                    <p className="text-meta text-[var(--ds-text-secondary)]">
                      Qty: {item.quantity} × {formatMoney(item.unit_price || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {draft.error_message && (
          <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded text-red-800 dark:text-red-200">
            <p className="text-body font-medium mb-1">Error:</p>
            <p className="text-meta">{draft.error_message}</p>
          </div>
        )}

        {showActions && canApproveOrReject && (onApprove || onReject) && (
          <div className="flex gap-2">
            {onApprove && (
              <Button
                onClick={() => onApprove(draft.id)}
                className="flex-1"
                variant="primary"
                size="sm"
                disabled={approving || rejecting}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {approving ? 'Approving…' : 'Approve'}
              </Button>
            )}
            {onReject && (
              <Button
                onClick={() => onReject(draft.id)}
                variant="outline"
                size="sm"
                disabled={approving || rejecting}
              >
                <XCircle className="w-4 h-4 mr-2" />
                {rejecting ? 'Rejecting…' : 'Reject'}
              </Button>
            )}
          </div>
        )}

        {showDownloadFile && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                window.open(`/api/ai-purchases/drafts/${draft.id}/file`, '_blank');
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        )}
      </div>

      {showSupplierPicker && (
        <SupplierPicker
          onSelect={handleSupplierSelect}
          onClose={() => setShowSupplierPicker(false)}
          showModal={true}
        />
      )}

      {showCreateSupplier && (
        <SlideOver
          open={showCreateSupplier}
          onOpenChange={setShowCreateSupplier}
          title="Create New Supplier"
          width="w-full max-w-2xl"
        >
          <form onSubmit={handleCreateSupplier} className="space-y-4">
            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supplier Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="supplier_name"
                value={supplierFormData.supplier_name || ''}
                onChange={handleSupplierFormChange}
                required
                placeholder="ABC Electrical Supplies Ltd"
                className="h-8"
              />
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supplier Code
              </label>
              <Input
                type="text"
                name="supplier_code"
                value={supplierFormData.supplier_code || ''}
                onChange={handleSupplierFormChange}
                placeholder="ABC-001"
                className="h-8"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Name
                </label>
                <Input
                  type="text"
                  name="contact_name"
                  value={supplierFormData.contact_name || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="John Smith"
                  className="h-8"
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  name="email"
                  value={supplierFormData.email || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="supplier@example.com"
                  className="h-8"
                />
              </div>
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={supplierFormData.phone || ''}
                onChange={handleSupplierFormChange}
                placeholder="+44 20 1234 5678"
                className="h-8"
              />
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address Line 1
              </label>
              <Input
                type="text"
                name="address_line1"
                value={supplierFormData.address_line1 || ''}
                onChange={handleSupplierFormChange}
                placeholder="123 High Street"
                className="h-8"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City
                </label>
                <Input
                  type="text"
                  name="city"
                  value={supplierFormData.city || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="London"
                  className="h-8"
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Postcode
                </label>
                <Input
                  type="text"
                  name="postcode"
                  value={supplierFormData.postcode || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="SW1A 1AA"
                  className="h-8"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCreateSupplier(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={creatingSupplier}
              >
                {creatingSupplier ? 'Creating...' : 'Create & Link Supplier'}
              </Button>
            </div>
          </form>
        </SlideOver>
      )}

      {PanelToastContainer && <PanelToastContainer />}
    </>
  );
}